<template>
  <dd-modal
    v-model:visible="internalShowModal"
    :subtitle="subtitle"
    class="workflow-status-transitions-modal"
    title="Status transitions"
    @handle-close="closeModal">
    <!-- Not adding to <dd-modal ..> or to parent's instance caller because this prevents parent page 
      from scrolling again after submit, since the v-if destroys the modal before it can finish closing -->
    <template v-if="hasCurrentStatusID"> 
      <div class="mock-status-dropdown-container">
        <div class="mock-status-dropdown">
          <div class="mock-status-dropdown__current-status">
            <status-chicklet :status="currentStatus" />
            <i class="ddi ddi-caret-down" />
          </div>
          <div class="mock-status-dropdown__available-statuses">
            <div 
              v-for="status in availableStatusesToTransitionTo" 
              :key="status.id" 
              :class="{'selected': isSelectedStatusTransition(status)}"
              @click="toggleStatusTransition(status)">
              <status-chicklet :status="status" />       
              <i v-if="isSelectedStatusTransition(status)" class="ddi ddi-check" />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <span>
        <dd-button type="text" @click="closeModal">Cancel</dd-button>
        <dd-button
          :is-disabled="hasNoStatusTransitions"
          :is-loading="isSubmitting" 
          :color="submitBtnColor" 
          @click="updateStatusTransitions()">Update</dd-button>
      </span>
    </template>
  </dd-modal>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { EEntities } from '@/types'
import {
  IStatusForSelection,
  IWorkflowDetail,
} from '@/api/types/response.types'
import { IWorkflowsInputUpdateStatusTransitions } from '@/api/types/request.types'

/* API */
import WorkflowsAPI from '@/api/modules/workflows.api'
import typeAssistant from '@/helpers/type-assistant'

export default defineComponent({
  name: 'WorkflowStatusTransitionsModal',

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    workflow: {
      type: Object as () => IWorkflowDetail,
      required: true,
    },
    currentStatusId: {
      type: Number,
      default: null,
    },
  },

  emits: ['status-transitions-updated', 'update:showModal'],

  data() {
    return {
      isSubmitting: false,
      transtionableStatusIDs: [] as number[],
      entityType: EEntities.PROJECTS,
    }
  },

  computed: {
    internalShowModal: {
      get(): boolean {
        return this.showModal
      },
      set(newValue: boolean) {
        this.$emit('update:showModal', newValue)
      },
    },
    hasCurrentStatusID(): boolean {
      return this.currentStatusId !== null
    },
    availableStatusesToTransitionTo(): IStatusForSelection[] {
      return this.workflow.listStatuses.filter(
        s => s.id !== this.currentStatusId,
      )
    },
    hasNoStatusTransitions(): boolean {
      return this.transtionableStatusIDs.length === 0
    },
    currentStatus(): IStatusForSelection {
      return this.workflow.listStatuses.find(s => s.id === this.currentStatusId)
    },
    submitBtnColor(): string {
      return ColorAssistant.getEntityColor(this.entityType)
    },
    subtitle(): string {
      if (this.hasCurrentStatusID) {
        return `When a task is set to <strong>${
          this.currentStatus.name
        }</strong>, the status can be updated to any of the following…`
      }
      return ''
    },
  },

  watch: {
    currentStatusId: {
      immediate: true,
      handler(val) {
        if (val === null) {
          return
        }
        this.transtionableStatusIDs = this.workflow.listStatusTransitions
          .filter(t => t.statusIDFrom === this.currentStatusId)
          .map(t => t.statusIDTo)
      },
    },
  },

  created() {
    this.entityType = typeAssistant.getEntityType(this.workflow.entityTypeID)
  },

  methods: {
    closeModal() {
      this.internalShowModal = false
    },
    isSelectedStatusTransition(status: IStatusForSelection) {
      return this.transtionableStatusIDs.includes(status.id)
    },
    toggleStatusTransition(status: IStatusForSelection) {
      if (this.isSelectedStatusTransition(status)) {
        this.transtionableStatusIDs = this.transtionableStatusIDs.filter(
          id => id !== status.id,
        )
      } else {
        this.transtionableStatusIDs.push(status.id)
      }
    },
    async updateStatusTransitions() {
      try {
        this.isSubmitting = true
        const data = {
          id: this.currentStatusId,
          listAvailableStatusesToMoveToIDs: this.transtionableStatusIDs,
        } as IWorkflowsInputUpdateStatusTransitions

        const response = await WorkflowsAPI.put_UpdateStatusTransitions(
          this.workflow.id,
          data,
        )
        this.$emit('status-transitions-updated')
        this.closeModal()
      } catch (err) {
        this.$error.emit(err, true)
        return false
      } finally {
        this.isSubmitting = false
      }
    },
  },
})
</script>

<style lang="scss">
.workflow-status-transitions-modal {
  .mock-status-dropdown-container {
    display: flex;
    justify-content: center;
    background-color: var(--color-background-primary);
    border: $border--solid-extra-light;
    padding: 20px 0;
  }

  .mock-status-dropdown {
    display: flex;
    flex-direction: column;
    border: $border--solid-light;
    border-radius: 8px;
    width: 400px;
    overflow: hidden;
    background-color: var(--color-background-header);

    &__current-status {
      padding: 10px 30px 10px 14px;
      border-bottom: $border--solid-light;
      display: flex;
      align-items: center;
      justify-content: space-between;
      @include flex-row-horizontal-spacing(4px);

      .ddi-caret-down {
        color: var(--color-text-third);
        font-size: $dd2-size--dropdown-caret;
        line-height: 14px;
      }
    }

    &__available-statuses {
      display: flex;
      flex-direction: column;
      padding: 4px;
      overflow-y: scroll;
      height: 200px;

      > div {
        display: flex;
        justify-content: space-between;
        padding: 6px 10px;
        border-radius: 4px;
        cursor: pointer;
        opacity: 0.7;

        &.selected {
          opacity: 1;
          .ddi {
            color: $color--blue;
          }
          &:hover {
            background-color: var(--color-background-secondary);
          }
        }
      }
    }
  }
}
</style>
